/**
 * Quick Layout Dialog — 2-click smart rack placement modal.
 *
 * Replaces the 6-step bulk placement wizard for the most common workflow:
 * placing ALL unplaced racks at once using intelligent warehouse patterns.
 *
 * Flow: Open dialog → (optionally adjust template/sliders) → click "Plasser".
 */

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  X,
  Rows3,
  Rows4,
  CornerDownRight,
  Square,
  ArrowRightLeft,
  LayoutGrid,
} from 'lucide-react';
import { useLayoutEditor } from '../contexts/LayoutEditorContext';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import {
  SmartLayoutConfig,
  LayoutMetrics,
  LayoutTemplateName,
  PreviewRackPosition,
  DEFAULT_SMART_LAYOUT_CONFIG,
} from '../types/warehouseLayout';
import {
  generateWarehouseLayout,
  estimateRequiredDimensions,
  LAYOUT_TEMPLATES,
} from '../utils/smartLayout';
import {
  computeRenderContext,
  drawBackground,
  drawFloor,
  drawPreviewRacks,
} from '../utils/canvasRenderers';
import { applyPreviewPositions } from '../utils/bulkPlacement';

// ============================================
// Template Icon Mapping
// ============================================

const TEMPLATE_ICONS: Record<LayoutTemplateName, React.FC<{ className?: string }>> = {
  'back-to-back': Rows4,
  'parallel-rows': Rows3,
  'u-shape': LayoutGrid,
  'l-shape': CornerDownRight,
  'perimeter': Square,
};

// ============================================
// Miniature Preview Canvas
// ============================================

interface MiniPreviewProps {
  positions: PreviewRackPosition[];
  warehouseLength: number;
  warehouseWidth: number;
}

const MiniPreview: React.FC<MiniPreviewProps> = ({ positions, warehouseLength, warehouseWidth }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { rackDimensionsMap } = useLayoutEditor();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    const dims = { length: warehouseLength, width: warehouseWidth, height: 8 };
    const rc = computeRenderContext(ctx, rect.width, rect.height, dims, 1, { x: 0, y: 0 });

    drawBackground(rc);
    drawFloor(rc, dims);
    drawPreviewRacks(rc, positions, rackDimensionsMap);
  }, [positions, warehouseLength, warehouseWidth, rackDimensionsMap]);

  return (
    <canvas
      ref={canvasRef}
      className="w-full h-full rounded-lg"
      style={{ imageRendering: 'auto' }}
    />
  );
};

// ============================================
// Quick Layout Dialog
// ============================================

const QuickLayoutDialog: React.FC = () => {
  const { state, dispatch, rackDimensionsMap } = useLayoutEditor();
  const { t } = useTranslation();
  const { layout, unplacedRacks, showQuickLayout } = state;

  const [config, setConfig] = useState<SmartLayoutConfig>(DEFAULT_SMART_LAYOUT_CONFIG);
  const [preview, setPreview] = useState<PreviewRackPosition[]>([]);
  const [metrics, setMetrics] = useState<LayoutMetrics | null>(null);
  const [autoGrow, setAutoGrow] = useState(true);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Effective dimensions (auto-grown or current)
  const effectiveDimensions = useMemo(() => {
    if (!layout) return { length: 30, width: 20, height: 8 };
    if (!autoGrow) return layout.dimensions;

    const estimated = estimateRequiredDimensions(unplacedRacks, rackDimensionsMap, config);
    return {
      length: Math.max(layout.dimensions.length, estimated.length),
      width: Math.max(layout.dimensions.width, estimated.width),
      height: layout.dimensions.height,
    };
  }, [layout, unplacedRacks, rackDimensionsMap, config, autoGrow]);

  // Recalculate preview when config changes (debounced)
  useEffect(() => {
    if (!layout || unplacedRacks.length === 0) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const result = generateWarehouseLayout(
        unplacedRacks,
        layout.racks,
        rackDimensionsMap,
        effectiveDimensions,
        config,
        layout.gridUnit
      );
      setPreview(result.positions);
      setMetrics(result.metrics);
    }, 150);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [config, layout, unplacedRacks, rackDimensionsMap, effectiveDimensions]);

  const handleApply = useCallback(() => {
    if (!layout) return;
    const validPositions = applyPreviewPositions(preview);
    if (validPositions.length === 0) return;

    // If auto-grow is enabled and dimensions expanded, update dimensions first
    if (autoGrow && (effectiveDimensions.length > layout.dimensions.length || effectiveDimensions.width > layout.dimensions.width)) {
      dispatch({
        type: 'SET_LAYOUT',
        layout: { ...layout, dimensions: effectiveDimensions },
        pushHistory: false,
      });
    }

    dispatch({
      type: 'APPLY_SMART_LAYOUT',
      positions: validPositions,
      metrics: metrics || { totalRacks: 0, placedRacks: 0, failedRacks: 0, spaceUtilization: 0, aisleCount: 0, rowCount: 0 },
    });
  }, [layout, preview, metrics, dispatch, autoGrow, effectiveDimensions]);

  const handleClose = useCallback(() => {
    dispatch({ type: 'CLOSE_QUICK_LAYOUT' });
  }, [dispatch]);

  const updateConfig = useCallback((updates: Partial<SmartLayoutConfig>) => {
    setConfig(prev => ({ ...prev, ...updates }));
  }, []);

  // Handle template selection (apply template defaults)
  const selectTemplate = useCallback((name: LayoutTemplateName) => {
    const template = LAYOUT_TEMPLATES.find(t => t.name === name);
    if (template) {
      setConfig(prev => ({
        ...prev,
        template: name,
        backToBack: name === 'back-to-back',
        ...(template.defaultConfig as Partial<SmartLayoutConfig>),
      }));
    }
  }, []);

  if (!showQuickLayout || !layout) return null;

  const validCount = preview.filter(p => p.isValid).length;
  const invalidCount = preview.filter(p => !p.isValid).length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-2xl shadow-2xl w-[580px] max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-5 pb-3 border-b">
          <div>
            <h2 className="text-lg font-bold text-gray-900">{t('manager.modules.warehouseLayout.quickLayout.title', {}, 'Hurtig Layout')}</h2>
            <p className="text-sm text-gray-500">{t('manager.modules.warehouseLayout.quickLayout.racksToPlace', { '0': unplacedRacks.length }, `${unplacedRacks.length} reoler å plassere`)}</p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">

          {/* Template Selection */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">{t('manager.modules.warehouseLayout.quickLayout.selectTemplate', {}, 'Velg mal')}</label>
            <div className="grid grid-cols-5 gap-2">
              {LAYOUT_TEMPLATES.map(template => {
                const Icon = TEMPLATE_ICONS[template.name];
                const isActive = config.template === template.name;
                const templateKey = template.name.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
                return (
                  <button
                    key={template.name}
                    onClick={() => selectTemplate(template.name)}
                    className={`flex flex-col items-center gap-1 p-3 rounded-xl border-2 transition-all text-center ${
                      isActive
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-200 hover:border-gray-300 text-gray-600 hover:bg-gray-50'
                    }`}
                    title={t(`manager.modules.warehouseLayout.templates.${templateKey}.description`, {}, template.description)}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="text-xs font-medium leading-tight">{t(`manager.modules.warehouseLayout.templates.${templateKey}.label`, {}, template.label)}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Orientation */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">{t('manager.modules.warehouseLayout.quickLayout.orientation', {}, 'Retning')}</label>
            <div className="flex gap-2">
              <button
                onClick={() => updateConfig({ orientation: 'horizontal' })}
                className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg border-2 transition-all ${
                  config.orientation === 'horizontal'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 hover:border-gray-300 text-gray-600'
                }`}
              >
                <ArrowRightLeft className="w-4 h-4" />
                <span className="text-sm font-medium">{t('manager.modules.warehouseLayout.quickLayout.horizontal', {}, 'Horisontal')}</span>
              </button>
              <button
                onClick={() => updateConfig({ orientation: 'vertical' })}
                className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg border-2 transition-all ${
                  config.orientation === 'vertical'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 hover:border-gray-300 text-gray-600'
                }`}
              >
                <ArrowRightLeft className="w-4 h-4 rotate-90" />
                <span className="text-sm font-medium">{t('manager.modules.warehouseLayout.quickLayout.vertical', {}, 'Vertikal')}</span>
              </button>
            </div>
          </div>

          {/* Sliders */}
          <div className="space-y-3">
            <SliderInput
              label={t('manager.modules.warehouseLayout.quickLayout.aisleWidth', {}, 'Gangbredde')}
              value={config.aisleWidth}
              min={1.5}
              max={4.0}
              step={0.1}
              unit="m"
              onChange={v => updateConfig({ aisleWidth: v })}
            />
            <SliderInput
              label={t('manager.modules.warehouseLayout.quickLayout.rackSpacing', {}, 'Reol-avstand')}
              value={config.rackSpacing}
              min={0.2}
              max={2.0}
              step={0.1}
              unit="m"
              onChange={v => updateConfig({ rackSpacing: v })}
            />
            <SliderInput
              label={t('manager.modules.warehouseLayout.quickLayout.wallMargin', {}, 'Veggavstand')}
              value={config.wallMargin}
              min={0.5}
              max={3.0}
              step={0.1}
              unit="m"
              onChange={v => updateConfig({ wallMargin: v })}
            />
            {config.backToBack && (
              <SliderInput
                label={t('manager.modules.warehouseLayout.quickLayout.backToBackGap', {}, 'Rygg-mot-rygg gap')}
                value={config.backToBackGap}
                min={0.1}
                max={0.5}
                step={0.05}
                unit="m"
                onChange={v => updateConfig({ backToBackGap: v })}
              />
            )}
          </div>

          {/* Auto-grow toggle */}
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={autoGrow}
              onChange={e => setAutoGrow(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-blue-600"
            />
            {t('manager.modules.warehouseLayout.quickLayout.autoGrow', {}, 'Utvid lagerdimensjoner automatisk ved behov')}
          </label>

          {/* Miniature Preview */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">{t('manager.modules.warehouseLayout.quickLayout.preview', {}, 'Forhåndsvisning')}</label>
            <div className="h-48 bg-gray-900 rounded-xl overflow-hidden">
              <MiniPreview
                positions={preview}
                warehouseLength={effectiveDimensions.length}
                warehouseWidth={effectiveDimensions.width}
              />
            </div>
          </div>

          {/* Metrics */}
          {metrics && (
            <div className="flex flex-wrap gap-3 text-sm">
              <div className={`px-3 py-1.5 rounded-lg ${validCount > 0 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                {t('manager.modules.warehouseLayout.metrics.placedOfTotal', { '0': validCount, '1': metrics.totalRacks }, `${validCount}/${metrics.totalRacks} plassert`)}
              </div>
              {invalidCount > 0 && (
                <div className="px-3 py-1.5 rounded-lg bg-red-100 text-red-700">
                  {t('manager.modules.warehouseLayout.quickLayout.collides', { '0': invalidCount }, `${invalidCount} kolliderer`)}
                </div>
              )}
              <div className="px-3 py-1.5 rounded-lg bg-blue-100 text-blue-800">
                {t('manager.modules.warehouseLayout.metrics.utilization', { '0': (metrics.spaceUtilization * 100).toFixed(0) }, `${(metrics.spaceUtilization * 100).toFixed(0)}% utnyttelse`)}
              </div>
              <div className="px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700">
                {t('manager.modules.warehouseLayout.metrics.rows', { '0': metrics.rowCount }, `${metrics.rowCount} rader`)}
              </div>
              {metrics.aisleCount > 0 && (
                <div className="px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700">
                  {t('manager.modules.warehouseLayout.metrics.aisles', { '0': metrics.aisleCount }, `${metrics.aisleCount} ganger`)}
                </div>
              )}
              {autoGrow && (effectiveDimensions.length > (layout?.dimensions.length || 0) || effectiveDimensions.width > (layout?.dimensions.width || 0)) && (
                <div className="px-3 py-1.5 rounded-lg bg-amber-100 text-amber-800">
                  {t('manager.modules.warehouseLayout.quickLayout.warehouseExpands', { '0': effectiveDimensions.length, '1': effectiveDimensions.width }, `Lager utvides til ${effectiveDimensions.length}m × ${effectiveDimensions.width}m`)}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-5 pt-3 border-t bg-gray-50">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
          >
            {t('common.buttons.cancel', {}, 'Avbryt')}
          </button>
          <button
            onClick={handleApply}
            disabled={validCount === 0}
            className="px-6 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('manager.modules.warehouseLayout.placement.placeNRacks', { '0': validCount }, `Plasser ${validCount} reoler`)}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================
// Slider Input Component
// ============================================

interface SliderInputProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  unit: string;
  onChange: (value: number) => void;
}

const SliderInput: React.FC<SliderInputProps> = ({
  label,
  value,
  min,
  max,
  step,
  unit,
  onChange,
}) => (
  <div className="flex items-center gap-3">
    <label className="text-sm text-gray-600 w-36 flex-shrink-0">{label}</label>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={e => onChange(parseFloat(e.target.value))}
      className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
    />
    <span className="text-sm font-mono text-gray-700 w-14 text-right">
      {value.toFixed(1)}{unit}
    </span>
  </div>
);

export default QuickLayoutDialog;
